import React, { useState, useCallback, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FiFilter, FiPlus } from 'react-icons/fi';
import { useTheme } from 'styled-components';

import CloseIcon from '~/icons/components/CloseIcon';
import {
  ProductReviewsContext,
  ReviewsFilters,
  OverallRating,
  ReviewsSizeSelector,
  useReviewsDrawer,
  Separator,
} from '~/techstyle-shared/react-components';
import {
  FormattedMessage,
  FormattedNumber,
} from '~/techstyle-shared/react-intl';
import { usePaginatedReviewsQuery } from '~/techstyle-shared/react-products';
import { VirtualScroll } from '~/techstyle-shared/react-virtual-scroll';

import { itemSoldOutStyle } from '../../../styles/genericSelectorStyles';
import { OverallFitPosition } from '../../constants/productDetail';
import RecentReviews from '../RecentReviews';

import {
  SortFilterDrawer,
  DrawerContentWrapper,
  FilterButton,
  FilterButtonLabel,
  showMoreButtonStyle,
  blockStyle,
  reviewsFiltersStyles,
  overallFitStyle,
  inputSizeStyle,
  RowWrapper,
  ResultCount,
  itemSizeLabelStyle,
  ScrollWrapper,
  FilterCount,
  FilterActiveDot,
  OverallFitSlider,
  separatorStyle,
} from './DrawerStyledComponents';

const PAGE_SIZE = 10;

export default function ReviewsDrawerContent({ overallFit }) {
  const {
    recentReviewsStyle: { productInfoStyle },
    overallRatingStyles,
  } = useTheme();

  const {
    openPhotoDrawer,
    sortBy,
    setSortBy,
    defaultSortBy,
    rating,
    setRating,
    filteredSizes,
    setFilteredSizes,
    otherSelectors,
    setOtherSelectors,
    groupCode,
    clearAllFilters,
    numSelectedFilters,
    sizesToQuery,
    counts: productReviewCounts,
  } = useReviewsDrawer();
  const theme = useTheme();
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [selectedSortValue, setSelectedSortValue] = useState(sortBy);
  const [selectedSizes, setSelectedSizes] = useState(filteredSizes);
  const [selectedOthers, setSelectedOthers] = useState(otherSelectors);

  const { data: paginatedReviewsData, fetchMore: loadPaginatedReviews } =
    usePaginatedReviewsQuery({
      groupCode,
      limit: PAGE_SIZE,
      offset: 0,
      sortBy,
      rating,
      sizes: sizesToQuery,
    });

  const paginatedReviews = paginatedReviewsData?.paginatedReviews ?? [];

  useEffect(() => {
    setSortBy(defaultSortBy);
    setSelectedSortValue(defaultSortBy);
  }, [rating, defaultSortBy, setSortBy]);

  useEffect(() => {
    setSelectedSizes(filteredSizes);
    setSelectedOthers(otherSelectors);
  }, [filteredSizes, otherSelectors]);

  const handleCloseFilterDrawer = useCallback(() => {
    setSelectedSizes(filteredSizes);
    setSelectedOthers(otherSelectors);
    setSelectedSortValue(sortBy);
    setFiltersDrawerOpen(false);
  }, [filteredSizes, sortBy, otherSelectors]);

  const handleApply = useCallback(() => {
    setSortBy(selectedSortValue);
    setFilteredSizes(selectedSizes);
    setOtherSelectors(selectedOthers);
    setFiltersDrawerOpen(false);
  }, [
    selectedOthers,
    selectedSizes,
    selectedSortValue,
    setFilteredSizes,
    setOtherSelectors,
    setSortBy,
  ]);

  const renderSizeFilterComponent = useCallback(
    () => (
      <>
        <ReviewsSizeSelector
          itemSizeLabelStyle={itemSizeLabelStyle}
          inputSizeStyle={inputSizeStyle}
          itemSoldOutStyle={itemSoldOutStyle}
          selectedItems={selectedSizes}
          addItem={(newItem) => setSelectedSizes([...selectedSizes, newItem])}
          removeItem={(oldItem) =>
            setSelectedSizes(selectedSizes.filter((item) => item !== oldItem))
          }
        />
      </>
    ),
    [selectedSizes]
  );

  const getItems = useCallback(
    ({ startIndex, stopIndex }) => {
      if (!paginatedReviews[startIndex]) {
        return null;
      }
      const items = paginatedReviews.slice(startIndex, stopIndex);

      const length = stopIndex - startIndex;

      if (items.length < length) {
        return items;
      }

      if (items.length > length) {
        items.length = length;
      }

      return items;
    },
    [paginatedReviews]
  );

  const renderItems = useCallback(
    ({ items }) => (
      // Render loading state reviews when `items` isn't available yet.
      <ProductReviewsContext reviews={items || Array(10).fill(true)}>
        <RecentReviews
          loading={!items}
          vertical
          blockStyle={blockStyle}
          productInfoStyle={productInfoStyle}
          onPictureClick={openPhotoDrawer}
          showMoreTextProps={{
            maxLength: 80,
            mobileMaxLength: 80,
            buttonStyle: showMoreButtonStyle,
          }}
        />
      </ProductReviewsContext>
    ),
    [openPhotoDrawer, productInfoStyle]
  );

  const loadItems = useCallback(
    async ({ startIndex, stopIndex }) => {
      try {
        await loadPaginatedReviews({
          variables: {
            offset: startIndex,
          },
        });
        // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    [loadPaginatedReviews]
  );

  // We know when we reached the end of results when a page does not return the PAGE_SIZE
  const hasReachedEnd = paginatedReviews.length % PAGE_SIZE !== 0;

  // Add on item count as we load more data, this will make sure we don't load thousands of batches.
  const itemCount = Math.min(
    hasReachedEnd
      ? paginatedReviews.length
      : paginatedReviews.length + PAGE_SIZE,
    productReviewCounts.filteredCount
  );

  return (
    <>
      {typeof overallFit === 'number' && (
        <>
          <OverallFitSlider
            minPosition={OverallFitPosition.MIN}
            maxPosition={OverallFitPosition.MAX}
            sliderPosition={overallFit}
            overallFitStyle={overallFitStyle}
            data-testid="reviews-overall-fit-slider"
          />
          <Separator
            color={theme.colors.productDetailSeparator}
            thickness={1}
            separatorStyle={separatorStyle}
          />
        </>
      )}
      <OverallRating
        counts={productReviewCounts}
        activeRating={rating}
        onRatingBarClickHandler={(val) => {
          if (rating === val) {
            setRating(undefined);
          } else {
            setRating(val);
          }
        }}
        {...overallRatingStyles}
      />
      <DrawerContentWrapper>
        <RowWrapper>
          <FilterButton
            onClick={() => setFiltersDrawerOpen(true)}
            data-testid="reviews-filter-btn"
          >
            <FiFilter />
            <FilterButtonLabel>
              <FormattedMessage
                id="site_pdp.filter_and_sort_header"
                defaultMessage="Filter & Sort"
              />
              {numSelectedFilters > 0 && (
                <FilterCount data-testid="reviews-filter-count">
                  {' '}
                  ({numSelectedFilters})<FilterActiveDot />{' '}
                </FilterCount>
              )}
            </FilterButtonLabel>
            <FiPlus size="12" />
          </FilterButton>

          {productReviewCounts && productReviewCounts.count && (
            <ResultCount>
              <FormattedMessage
                id="site_pdp.reviews_total_results"
                defaultMessage="{count} results"
                values={{
                  count: (
                    <FormattedNumber
                      value={productReviewCounts.filteredCount}
                    />
                  ),
                }}
              />
            </ResultCount>
          )}
        </RowWrapper>
      </DrawerContentWrapper>

      <SortFilterDrawer
        isOpen={filtersDrawerOpen}
        onExit={handleCloseFilterDrawer}
        position="bottom"
        title="Filter drawer"
        data-testid="reviews-filter-drawer"
      >
        <DrawerContentWrapper>
          <ReviewsFilters
            closeIcon={<CloseIcon />}
            onCloseDrawerHandler={handleCloseFilterDrawer}
            onApplyHandler={handleApply}
            onClearAllHandler={clearAllFilters}
            sizeFilterComponent={renderSizeFilterComponent()}
            selectedSortValue={selectedSortValue}
            onRadioItemChange={(e) => {
              setSelectedSortValue(e.target.value);
            }}
            {...reviewsFiltersStyles}
          />
        </DrawerContentWrapper>
      </SortFilterDrawer>

      <ScrollWrapper>
        <VirtualScroll
          itemCount={itemCount}
          getItems={getItems}
          loadItems={loadItems}
          targetBatchSize={PAGE_SIZE}
          renderItems={renderItems}
        />
      </ScrollWrapper>
    </>
  );
}

ReviewsDrawerContent.propTypes = {
  overallFit: PropTypes.number,
};
